import clsx from 'clsx'
import React from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { DropdownItemSpec } from './DropdownCommonProps'
import DropdownToggle from './DropdownToggle'

interface MultiDropdownSelectedItemProps<T extends DropdownItemSpec<any>> {
  item: T
  itemToString: (item: T) => string
  // If true, show an exclusive (noCheckbox) option as plain text instead of a removable chip.
  noCheckboxAsLabel: boolean
  // If true, the chip won't show its × so it can't be removed (for example when
  // preventUnselectLast is on and only one item is left).
  disableUnselect: boolean
  // Called when the plain-label form is clicked, to open the dropdown menu. (Chips don't
  // use this — they are removed via their own × button.)
  onActivate: () => void
  getRemoveButtonProps: (args: { item: T; onClick: () => void }) => {
    onClick: (e: React.MouseEvent<HTMLElement>) => void
  }
  unselectItem: (item: T) => void
  classes: Record<string, string>
}

/**
 * Shows one chosen value inside a MultiDropdown's input box. It renders either:
 *  - plain text with no × (for an exclusive `noCheckbox` option, when `noCheckboxAsLabel`
 *    is set), or
 *  - a removable chip — a small tag with an × button to deselect it.
 *
 * This is built specifically for MultiDropdown's input box, not a reusable list item.
 */
export default function MultiDropdownSelectedItem<T extends DropdownItemSpec<any>>({
  item,
  itemToString,
  noCheckboxAsLabel,
  disableUnselect,
  onActivate,
  getRemoveButtonProps,
  unselectItem,
  classes,
}: MultiDropdownSelectedItemProps<T>) {
  if (noCheckboxAsLabel && item.noCheckbox) {
    return (
      <div
        title={itemToString(item)}
        onClick={onActivate}
        className={clsx(classes.placeholder, 'placeholder')}
      >
        {itemToString(item)}
      </div>
    )
  }

  const removable = !disableUnselect && !item.noCheckbox
  return (
    <div title={itemToString(item)} className={clsx(classes.selectedItem, 'selected-item')}>
      <span>{itemToString(item)}</span>
      {removable ? (
        <DropdownToggle
          {...getRemoveButtonProps({ item, onClick: () => unselectItem(item) })}
          aria-label="remove item"
          type="button"
        >
          <FontAwesomeIcon solid size="sm">
            xmark
          </FontAwesomeIcon>
        </DropdownToggle>
      ) : null}
    </div>
  )
}
